import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "@/assets/logo.png";
import logoText from "@/assets/logo-text.png";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  BookOpen,
  Layers,
  Video,
  FileText,
  ClipboardList,
  CalendarRange,
  Library,
  Leaf,
  LogOut,
  User as UserIcon,
  ChevronRight,
  ChevronDown,
  Settings as SettingsIcon,
  CreditCard,
  MessageSquare,
  Image,
  Bell,
  BookA,
  MessagesSquare,
  Compass,
  FolderOpen,
  Activity,
  FileCheck2,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { logout } from "@/redux/slices/authSlice";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface SubMenuItem {
  title: string;
  url: string;
  icon: any;
}

interface MenuItem {
  title: string;
  url: string;
  icon: any;
  subItems?: SubMenuItem[];
}

const main: MenuItem[] = [
  { title: "Dashboard", url: "/", icon: LayoutDashboard },
  { title: "Students", url: "/students", icon: Users },
  { title: "Chatting Groups", url: "/chat-groups", icon: MessagesSquare },
  { title: "Student Feedback", url: "/student-feedback", icon: MessageSquare },
];

const academics: MenuItem[] = [
  { title: "Academic Years", url: "/years", icon: CalendarDays },
  { title: "Subjects", url: "/subjects", icon: BookOpen },
  {
    title: "Explore Hub",
    url: "/explore",
    icon: Compass,
    subItems: [
      { title: "Hub Overview", url: "/explore", icon: Compass },
      { title: "Drive Resources", url: "/explore/drive", icon: FolderOpen },
      { title: "Dictionary", url: "/explore/dictionary", icon: BookA },
      { title: "Dravya Herbology", url: "/explore/dravya", icon: Leaf },
      { title: "Chikitsa Protocols", url: "/explore/chikitsa", icon: Activity },
      { title: "Submissions Queue", url: "/explore/submissions", icon: FileCheck2 },
    ],
  },
  { title: "Chapters", url: "/chapters", icon: Layers },
];

const content: MenuItem[] = [
  { title: "Videos", url: "/videos", icon: Video },
  { title: "Notes (PDF)", url: "/notes", icon: FileText },
  { title: "Home Carousels", url: "/carousels", icon: Image },
];

const exams: MenuItem[] = [
  { title: "Chapter Tests", url: "/chapter-tests", icon: ClipboardList },
  { title: "Weekly Exams", url: "/weekly-exams", icon: CalendarRange },
  { title: "Question Bank", url: "/question-bank", icon: Library },
];

const system: MenuItem[] = [
  { title: "Plan Management", url: "/plans/management", icon: Layers },
  { title: "Order History", url: "/orders", icon: ClipboardList },
  { title: "Push Notifications", url: "/notifications", icon: Bell },
  { title: "Communication", url: "/communication-settings", icon: MessageSquare },
  { title: "Settings", url: "/settings", icon: SettingsIcon },
];

export function AppSidebar() {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const tenant = useAppSelector((s) => s.auth.tenant);

  // Auto-expand Explore dropdown if current URL is under /explore or /dictionary
  const isExplorePath = pathname.startsWith("/explore") || pathname === "/dictionary";
  const [exploreExpanded, setExploreExpanded] = useState(isExplorePath);

  useEffect(() => {
    if (isExplorePath) {
      setExploreExpanded(true);
    }
  }, [pathname, isExplorePath]);

  const isActive = (url: string) => (url === "/" ? pathname === "/" : pathname.startsWith(url));

  const renderGroup = (label: string, items: MenuItem[]) => (
    <SidebarGroup className="px-3">
      {!collapsed && (
        <SidebarGroupLabel className="px-2 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 mb-2">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarGroupContent>
        <SidebarMenu className="gap-1">
          {items.map((item) => {
            // Handle expandable dropdown with subItems (Explore Hub)
            if (item.subItems && item.subItems.length > 0) {
              const isParentActive = isExplorePath;

              return (
                <SidebarMenuItem key={item.title}>
                  <div
                    onClick={() => {
                      if (collapsed) {
                        navigate(item.url);
                      } else {
                        setExploreExpanded(!exploreExpanded);
                      }
                    }}
                    className={cn(
                      "flex items-center justify-between h-10 transition-all duration-200 rounded-xl px-3 cursor-pointer select-none",
                      isParentActive
                        ? "bg-primary/10 text-primary font-bold"
                        : "hover:bg-primary/5 text-muted-foreground hover:text-primary"
                    )}
                  >
                    <div className="flex items-center gap-2.5 overflow-hidden">
                      <item.icon
                        className={cn(
                          "h-4 w-4 shrink-0 transition-colors",
                          isParentActive ? "text-primary" : "text-muted-foreground"
                        )}
                      />
                      {!collapsed && <span className="text-sm tracking-tight">{item.title}</span>}
                    </div>

                    {!collapsed && (
                      <ChevronDown
                        className={cn(
                          "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                          exploreExpanded && "rotate-180 text-primary"
                        )}
                      />
                    )}
                  </div>

                  {!collapsed && exploreExpanded && (
                    <div className="pl-4 pt-1 space-y-1">
                      {item.subItems.map((sub) => {
                        const isSubActive =
                          sub.url === "/explore"
                            ? pathname === "/explore"
                            : pathname === sub.url ||
                              (sub.url === "/explore/dictionary" && pathname === "/dictionary");

                        return (
                          <NavLink
                            key={sub.url}
                            to={sub.url}
                            end={sub.url === "/explore"}
                            onClick={() => {
                              if (isMobile) setOpenMobile(false);
                            }}
                            className={cn(
                              "flex items-center gap-2.5 h-8 px-2.5 rounded-lg text-xs font-semibold transition-all",
                              isSubActive
                                ? "bg-primary text-primary-foreground font-bold shadow-xs"
                                : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                            )}
                          >
                            <sub.icon
                              className={cn(
                                "h-3.5 w-3.5 shrink-0",
                                isSubActive ? "text-primary-foreground" : "text-muted-foreground"
                              )}
                            />
                            <span className="truncate">{sub.title}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </SidebarMenuItem>
              );
            }

            // Normal single menu item
            return (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton
                  asChild
                  isActive={isActive(item.url)}
                  className={cn(
                    "h-10 transition-all duration-300 rounded-xl px-3",
                    isActive(item.url)
                      ? "bg-primary text-primary-foreground font-bold"
                      : "hover:bg-primary/5 text-muted-foreground hover:text-primary"
                  )}
                >
                  <NavLink
                    to={item.url}
                    end={item.url === "/"}
                    onClick={() => {
                      if (isMobile) {
                        setOpenMobile(false);
                      }
                    }}
                  >
                    <item.icon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isActive(item.url) ? "text-primary-foreground" : "text-muted-foreground"
                      )}
                    />
                    {!collapsed && <span className="text-sm tracking-tight">{item.title}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-border/50 bg-card/30 backdrop-blur-xl">
      <SidebarHeader className="p-4">
        <div className={cn("flex items-center gap-3 transition-all duration-300", collapsed && "justify-center px-0")}>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white border shadow-none ring-1 ring-black/5 overflow-hidden">
            <img src={logo} className="h-8 w-8 object-contain" />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <img src={logoText} className="h-6 object-contain" alt={tenant?.name || "Ayursangrah"} />
              <p className="truncate text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-70 mt-1">
                {tenant?.name || "Institute Admin"}
              </p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <div className="flex-1 overflow-y-auto px-1">
        <SidebarContent className="py-4 gap-6">
          {renderGroup("Overview", main)}
          {renderGroup("Academics", academics)}
          {renderGroup("Content", content)}
          {renderGroup("Exams", exams)}
          {renderGroup("System", system)}
        </SidebarContent>
      </div>

      <SidebarFooter className="p-4 border-t border-border/50 bg-muted/5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex w-full items-center gap-3 rounded-2xl p-2 transition-all hover:bg-primary/5 group",
                collapsed && "justify-center p-1"
              )}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                <UserIcon className="h-4 w-4" />
              </div>
              {!collapsed && (
                <div className="flex-1 text-left min-w-0">
                  <p className="truncate text-sm font-bold text-foreground leading-tight">
                    {tenant?.name || user?.name || "Admin"}
                  </p>
                  <p className="truncate text-[10px] font-medium text-muted-foreground">
                    {user?.email || "admin@medicos.com"}
                  </p>
                </div>
              )}
              {!collapsed && (
                <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side="right"
            className="w-56 rounded-2xl p-2 shadow-2xl border-none ring-1 ring-black/5 mb-4 ml-4"
          >
            <div className="px-2 py-1.5 mb-2 border-b border-muted">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                Class Account
              </p>
              <p className="text-sm font-bold text-foreground truncate">{tenant?.name || "Ayursangrah"}</p>
              <p className="text-xs font-medium text-muted-foreground truncate">
                {user?.name} ({user?.email})
              </p>
            </div>
            <DropdownMenuItem
              onClick={() => {
                dispatch(logout());
                navigate("/login");
              }}
              className="rounded-xl text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer h-10 font-bold"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
